'use client'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { PlayMessageData } from '@/lib/pbx/flow/types'

interface PlayMessagePanelProps {
  data: PlayMessageData
  onUpdate: (data: Partial<PlayMessageData>) => void
}

const LANGUAGES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'de-DE', label: 'German' },
  { value: 'fr-FR', label: 'French' },
  { value: 'es-ES', label: 'Spanish' },
  { value: 'pl-PL', label: 'Polish' },
  { value: 'uk-UA', label: 'Ukrainian' },
  { value: 'ru-RU', label: 'Russian' },
]

const VOICES = [
  { value: 'alice', label: 'Alice (female)' },
  { value: 'Polly.Tatyana', label: 'Tatyana (female, RU)' },
  { value: 'Polly.Joanna', label: 'Joanna (female, EN)' },
  { value: 'Polly.Matthew', label: 'Matthew (male, EN)' },
]

export function PlayMessagePanel({ data, onUpdate }: PlayMessagePanelProps) {
  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <Label>Message Type</Label>
        <Select
          value={data.message_type || 'tts'}
          onValueChange={(value: 'tts' | 'audio') =>
            onUpdate({ message_type: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tts">Text to Speech</SelectItem>
            <SelectItem value="audio">Audio File</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {data.message_type === 'tts' ? (
        <>
          <div className="space-y-2">
            <Label htmlFor="tts_text">Text</Label>
            <Textarea
              id="tts_text"
              value={data.tts_text || ''}
              onChange={(e) => onUpdate({ tts_text: e.target.value })}
              placeholder="Enter text to speak..."
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label>Language</Label>
            <Select
              value={data.tts_language || 'en-US'}
              onValueChange={(value) => onUpdate({ tts_language: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Voice</Label>
            <Select
              value={data.tts_voice || 'alice'}
              onValueChange={(value) => onUpdate({ tts_voice: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VOICES.map((voice) => (
                  <SelectItem key={voice.value} value={voice.value}>
                    {voice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="audio_url">Audio URL</Label>
          <Input
            id="audio_url"
            value={data.audio_url || ''}
            onChange={(e) => onUpdate({ audio_url: e.target.value })}
            placeholder="https://example.com/message.mp3"
          />
          <p className="text-xs text-muted-foreground">
            Direct URL to MP3 or WAV file
          </p>
        </div>
      )}
    </div>
  )
}
